import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Loader2, MapPin, DollarSign,
  Clock, FileText, Sparkles, Hammer, Wrench, Zap, Trees, Car, Heart,
  Scissors, Truck, Paintbrush, Wind, Camera, GraduationCap, Dog, Dumbbell, MoreHorizontal,
  Image, Trash2
} from "lucide-react";

const iconMap = {
  Sparkles, Hammer, Wrench, Zap, Trees, Car, Heart, Scissors,
  Truck, Paintbrush, Wind, Camera, GraduationCap, Dog, Dumbbell, MoreHorizontal
};

// Helper function for budget display
const formatBudgetDisplay = (budgetMin, budgetMax) => {
  if (budgetMin && budgetMax) return `$${budgetMin} - $${budgetMax}`;
  if (budgetMax) return `Up to $${budgetMax}`;
  return `From $${budgetMin}`;
};

// Helper function for urgency button styling
const getUrgencyButtonStyle = (currentUrgency, optionValue) => {
  if (currentUrgency !== optionValue) return "border-gray-200 hover:border-gray-300";
  if (optionValue === "urgent") return "border-red-500 bg-red-50";
  return "border-forest bg-forest/5";
};

export default function PostJobPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const token = localStorage.getItem("token");
  const fileInputRef = useRef(null);

  // Helper function to get step label
  const getStepLabel = (currentStep) => {
    const labels = {
      1: "Service Type",
      2: "Job Details",
      3: "Budget & Timing"
    };
    return labels[currentStep] || "";
  };

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    subcategory: "",
    location: "",
    budget_min: "",
    budget_max: "",
    urgency: "standard",  // "standard" or "emergency"
    images: [],
    preferred_date: "",
    preferred_time: "",
    // BQL Express Bidding fields (OPTIONAL - not default)
    bidding_enabled: false,  // Customer opts in
    target_price: ""
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        toast.error("Failed to load categories");
      }
    };
    fetchCategories();
  }, []);

  const selectedCategory = categories.find(c => c.id === formData.category);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 4 - formData.images.length;
    
    if (files.length > remaining) {
      toast.error(`You can only add ${remaining} more image(s). Maximum is 4.`);
      return;
    }

    files.slice(0, remaining).forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max size is 5MB.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (ev) => {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ev.target.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null,
        images: formData.images.length > 0 ? formData.images : null,
        // BQL Express Bidding fields (OPTIONAL)
        bidding_enabled: formData.bidding_enabled,
        target_price: formData.bidding_enabled && formData.target_price 
          ? parseFloat(formData.target_price) 
          : null
      };

      await axios.post(`${API}/jobs`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Track Google Ads conversion for job posting
      if (window.trackConversion) {
        window.trackConversion('job_posted', 0);
      }
      toast.success(formData.bidding_enabled 
        ? "Job posted with Express Bidding! Up to 3 pros will compete with bids."
        : "Job posted successfully! Professionals will start responding soon."
      );
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to post job");
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = formData.category && formData.title.trim();
  const canProceedStep2 = formData.description.trim() && formData.location.trim();

  return (
    <div className="min-h-screen bg-sand">
      <Header />
      
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-3xl mx-auto" data-testid="post-job-page">
        <button
          onClick={() => step === 1 ? navigate("/dashboard") : setStep(step - 1)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {step === 1 ? "Back to Dashboard" : "Back"}
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-gray-900 mb-2">
            Post a New Job
          </h1>
          <p className="text-gray-600">
            Step {step} of 3 - {getStepLabel(step)}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`step-indicator ${step >= s ? "active" : "pending"}`}>
                {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className="w-12 md:w-20 h-1 rounded bg-gray-200">
                  <div className={`h-full rounded transition-all ${step > s ? "w-full bg-forest" : "w-0"}`} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <Card className="border-stone-200 shadow-lg">
          <CardContent className="pt-6">
            <form onSubmit={step === 3 ? handleSubmit : (e) => { e.preventDefault(); setStep(step + 1); }} data-testid="post-job-form">
              
              {/* Step 1: Service Type */}
              {step === 1 && (
                <div className="space-y-6">
                  <div className="space-y-3">
                    <Label className="text-base">What type of service do you need?</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {categories.map((category) => {
                        const IconComponent = iconMap[category.icon] || MoreHorizontal;
                        return (
                          <button
                            key={category.id}
                            type="button"
                            onClick={() => setFormData({ ...formData, category: category.id, subcategory: "" })}
                            className={`p-4 rounded-xl border text-left transition-all ${
                              formData.category === category.id
                                ? "border-forest bg-forest/5"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            data-testid={`category-option-${category.id}`}
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${
                              formData.category === category.id ? "bg-forest text-white" : "bg-gray-100 text-gray-600"
                            }`}>
                              <IconComponent className="w-5 h-5" />
                            </div>
                            <span className="font-medium text-sm text-gray-900">{category.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {selectedCategory && selectedCategory.subcategories?.length > 0 && (
                    <div className="space-y-2">
                      <Label htmlFor="subcategory">Specific Service (Optional)</Label>
                      <Select
                        value={formData.subcategory}
                        onValueChange={(value) => setFormData({ ...formData, subcategory: value })}
                      >
                        <SelectTrigger className="h-12" data-testid="subcategory-select">
                          <SelectValue placeholder="Select specific service" />
                        </SelectTrigger>
                        <SelectContent>
                          {selectedCategory.subcategories.map((sub) => (
                            <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="title">Job Title</Label>
                    <Input
                      id="title"
                      placeholder="e.g., Need pool cleaned weekly"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      className="h-12"
                      data-testid="job-title-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-forest hover:bg-forest-dark rounded-full text-base"
                    disabled={!canProceedStep1}
                    data-testid="step1-next-btn"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {/* Step 2: Job Details */}
              {step === 2 && (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="description">Describe the job in detail</Label>
                    <Textarea
                      id="description"
                      placeholder="Please include all relevant details about the job, any specific requirements, timing preferences, etc."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      className="min-h-[150px]"
                      data-testid="job-description-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="location"
                        placeholder="City, State or Address"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        required
                        className="h-12 pl-10"
                        data-testid="job-location-input"
                      />
                    </div>
                  </div>

                  {/* Image Upload Section */}
                  <div className="space-y-3">
                    <Label>Photos (Optional - up to 4)</Label>
                    <p className="text-sm text-gray-500">Add photos to help professionals understand the job better</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {formData.images.map((img, index) => (
                        <div key={`job-img-${index}-${img.slice(0, 20)}`} className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 group">
                          <img src={img} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-job-image-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      
                      {formData.images.length < 4 && (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-2 text-gray-400 hover:border-forest hover:text-forest transition-colors"
                          data-testid="add-job-image"
                        >
                          <Image className="w-8 h-8" />
                          <span className="text-xs">Add Photo</span>
                        </button>
                      )}
                    </div>
                    
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      multiple
                      className="hidden"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-forest hover:bg-forest-dark rounded-full text-base"
                    disabled={!canProceedStep2}
                    data-testid="step2-next-btn"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {/* Step 3: Budget & Timing */}
              {step === 3 && (
                <div className="space-y-6">
                  {/* Pricing Preference - BQL Express Bidding */}
                  <div className="space-y-3">
                    <Label>How would you like to receive quotes?</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, pricing_type: "quote_request", target_price: "" })}
                        className={`p-4 rounded-xl border text-center transition-all ${
                          formData.pricing_type === "quote_request" 
                            ? "border-[#1E40AF] bg-[#1E40AF]/5" 
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        data-testid="pricing-quote-request"
                      >
                        <span className="text-2xl mb-1 block">💬</span>
                        <span className="font-medium text-sm text-gray-900 block">Request Quotes</span>
                        <span className="text-xs text-gray-500">Let pros suggest their price</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, pricing_type: "target_price" })}
                        className={`p-4 rounded-xl border text-center transition-all ${
                          formData.pricing_type === "target_price" 
                            ? "border-green-500 bg-green-50" 
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        data-testid="pricing-target-price"
                      >
                        <span className="text-2xl mb-1 block">🎯</span>
                        <span className="font-medium text-sm text-gray-900 block">Set My Price</span>
                        <span className="text-xs text-gray-500">I know what I want to pay</span>
                      </button>
                    </div>
                    
                    {/* Target Price Input */}
                    {formData.pricing_type === "target_price" && (
                      <div className="mt-3 p-4 bg-green-50 rounded-xl border border-green-200">
                        <Label className="text-green-800 mb-2 block">Your Target Price</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-600" />
                          <Input
                            type="number"
                            placeholder="e.g., 150"
                            value={formData.target_price}
                            onChange={(e) => setFormData({ ...formData, target_price: e.target.value })}
                            className="h-12 pl-10 border-green-300 focus:border-green-500"
                            data-testid="target-price-input"
                          />
                        </div>
                        <p className="text-xs text-green-700 mt-2">Pros will see your target and can match or counter-offer</p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Budget Range (Optional)</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                          type="number"
                          placeholder="Min"
                          value={formData.budget_min}
                          onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                          className="h-12 pl-10"
                          data-testid="budget-min-input"
                        />
                      </div>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                          type="number"
                          placeholder="Max"
                          value={formData.budget_max}
                          onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                          className="h-12 pl-10"
                          data-testid="budget-max-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>How urgent is this job?</Label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { value: "standard", label: "Standard", desc: "Schedule at your convenience", icon: "📅" },
                        { value: "emergency", label: "Emergency", desc: "Need help ASAP!", icon: "🚨" }
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, urgency: option.value })}
                          className={`p-4 rounded-xl border text-center transition-all ${
                            formData.urgency === option.value 
                              ? option.value === "emergency" 
                                ? "border-red-500 bg-red-50" 
                                : "border-forest bg-forest/5"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          data-testid={`urgency-${option.value}`}
                        >
                          <span className="text-2xl mb-1 block">{option.icon}</span>
                          <span className="font-medium text-sm text-gray-900 block">{option.label}</span>
                          <span className="text-xs text-gray-500">{option.desc}</span>
                        </button>
                      ))}
                    </div>
                    {formData.urgency === "emergency" && (
                      <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                        Emergency requests may incur additional charges. Professionals will prioritize your job.
                      </p>
                    )}
                  </div>

                  {/* Preferred Date & Time */}
                  <div className="space-y-3">
                    <Label>When do you need the service? (Optional)</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="preferred_date" className="text-sm text-gray-500">Preferred Date</Label>
                        <Input
                          id="preferred_date"
                          type="date"
                          value={formData.preferred_date}
                          onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                          min={new Date().toISOString().split('T')[0]}
                          className="h-12"
                          data-testid="preferred-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="preferred_time" className="text-sm text-gray-500">Preferred Time</Label>
                        <Select
                          value={formData.preferred_time}
                          onValueChange={(value) => setFormData({ ...formData, preferred_time: value })}
                        >
                          <SelectTrigger className="h-12" data-testid="preferred-time-select">
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="morning">Morning (8am - 12pm)</SelectItem>
                            <SelectItem value="afternoon">Afternoon (12pm - 5pm)</SelectItem>
                            <SelectItem value="evening">Evening (5pm - 8pm)</SelectItem>
                            <SelectItem value="flexible">Flexible / Anytime</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="p-4 bg-sand rounded-xl">
                    <h4 className="font-medium text-gray-900 mb-3">Job Summary</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-gray-500">Category:</span> {selectedCategory?.name}</p>
                      <p><span className="text-gray-500">Title:</span> {formData.title}</p>
                      <p><span className="text-gray-500">Location:</span> {formData.location}</p>
                      <p>
                        <span className="text-gray-500">Pricing:</span>{" "}
                        {formData.pricing_type === "target_price" 
                          ? <span className="text-green-600 font-medium">🎯 Target Price: ${formData.target_price}</span>
                          : <span>💬 Requesting Quotes</span>
                        }
                      </p>
                      {(formData.budget_min || formData.budget_max) && (
                        <p>
                          <span className="text-gray-500">Budget Range:</span>{" "}
                          {formatBudgetDisplay(formData.budget_min, formData.budget_max)}
                        </p>
                      )}
                      <p>
                        <span className="text-gray-500">Urgency:</span>{" "}
                        <span className={formData.urgency === "emergency" ? "text-red-600 font-medium" : ""}>
                          {formData.urgency === "emergency" ? "🚨 Emergency (3 bids max)" : "📅 Standard"}
                        </span>
                      </p>
                      {formData.preferred_date && (
                        <p><span className="text-gray-500">Preferred Date:</span> {formData.preferred_date}</p>
                      )}
                      {formData.preferred_time && (
                        <p><span className="text-gray-500">Preferred Time:</span> {formData.preferred_time}</p>
                      )}
                      {formData.images.length > 0 && (
                        <p><span className="text-gray-500">Photos:</span> {formData.images.length} attached</p>
                      )}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-sienna hover:bg-sienna-dark rounded-full text-base"
                    disabled={loading}
                    data-testid="submit-job-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Posting Job...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Post Job
                      </>
                    )}
                  </Button>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
}
